import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import produce from 'immer';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

import { newsletterFrequencies } from 'shared/constants/news/newsletterFrequencies';
import {
  newsFeedTypes,
  newsToArtifactTypes,
  newsFeedTypesLabels,
} from 'shared/constants/news/newsSettings';
import {
  flattenIndustries,
  newsFeedSettingsParser,
  newsletterSettingsParser,
} from 'shared/parsers';
import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { useQuery } from 'shared/hooks';
import { GroupedSelect } from 'ui';
import { getParsedRegions, getNewsFeedPreferences, getAllIndustries } from 'selectors/common';
import { isEmpty } from 'lodash';

const useStyle = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3, 2),
  },
  space: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 14,
  },
  formControl: {
    width: '100%',
  },
  helperText: {
    marginTop: -theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  ultimateHelperText: {
    marginTop: -theme.spacing(2),
  },
}));

const NewsFeedSettings = ({
  followedAnalysts,
  newsletterEnabled,
  pristine,
  newsSettingsChanged,
  errorStateChanged,
}) => {
  const classes = useStyle();
  const { t } = useTranslation();
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const navigate = useNavigate();
  const { page } = useQuery();

  const { data: industriesData, isPending: isIndustriesDataPending } =
    useSelector(getAllIndustries);

  const [industries, setIndustries] = useState();

  const rawPreferences = useSelector(getNewsFeedPreferences);
  const [preferences, setPreferences] = useState({});

  useEffect(() => {
    if (isIndustriesDataPending || !industries) {
      return;
    }

    // Currently preferences and filtering APIs use different industries formats which becomes problematic on frontend side.
    // Therefore we have to modify industries returned by API to align with filtering format.
    const prefs = cloneDeep(rawPreferences);
    const classifications = get(prefs, 'newsFeed.classifications[0]');
    if (classifications) {
      classifications.values = classifications.values
        .map((industryCode) => industries.find(({ code }) => code === industryCode))
        .filter((industry) => industry);
    }
    setPreferences(prefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawPreferences, industries]);

  const allRegions = useSelector(getParsedRegions);

  const resetPageNumber = () => page && navigate('/');

  const buildInitialSettings = (prefs = {}) => ({
    newsFeed: newsFeedSettingsParser(prefs),
    newsletter: newsletterSettingsParser(prefs),
  });

  const [settings, setSettings] = useState(buildInitialSettings(preferences));
  const [error, setError] = useState(false);

  const isError = () => {
    const newsTypes = !newsletterEnabled
      ? settings.newsFeed.newsTypes
      : settings.newsletter.newsTypes;
    return !newsTypes || newsTypes.length <= 0;
  };

  useEffect(() => {
    if (!isEmpty(preferences)) {
      setSettings(buildInitialSettings(preferences));
    }
  }, [preferences]);

  useEffect(() => {
    newsSettingsChanged(settings);
    setError(isError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    errorStateChanged(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (pristine) {
      setSettings(buildInitialSettings(preferences));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pristine]);

  const disabled =
    newsletterEnabled && settings.newsletter.frequency === newsletterFrequencies.NONE;
  const showNewsTypes = !newsletterEnabled || !settings.newsletter.useNewsFeedPreferences;

  const handleFrequencyChange = (event) => {
    setSettings({
      ...settings,
      newsletter: { ...settings.newsletter, frequency: event.target.value },
    });
    resetPageNumber();
  };

  const handleNewsFeedCopyChange = (event) => {
    setSettings({
      ...settings,
      newsletter: { ...settings.newsletter, useNewsFeedPreferences: event.target.checked },
    });
    resetPageNumber();
  };

  const handleTypeChange = (newsType) => (event) => {
    const artifactTypes = newsToArtifactTypes[newsType];

    const newsTypes = !newsletterEnabled
      ? settings.newsFeed.newsTypes
      : settings.newsletter.newsTypes;

    const updatedNewsTypes = event.target.checked
      ? newsTypes.concat(...artifactTypes)
      : newsTypes.filter((el) => !artifactTypes.includes(el));

    setSettings(
      produce(settings, (draftSettings) => {
        if (!newsletterEnabled) {
          draftSettings.newsFeed.newsTypes = updatedNewsTypes; // eslint-disable-line no-param-reassign
        } else {
          draftSettings.newsletter.newsTypes = updatedNewsTypes; // eslint-disable-line no-param-reassign
        }
      }),
    );
    resetPageNumber();
  };

  const handleIndustriesChange = (_, values) => {
    const updatedIndustryTypes = values;

    setSettings(
      produce(settings, (draftSettings) => {
        if (!newsletterEnabled) {
          draftSettings.newsFeed.industryTypes = updatedIndustryTypes; // eslint-disable-line no-param-reassign
        } else {
          draftSettings.newsletter.industryTypes = updatedIndustryTypes; // eslint-disable-line no-param-reassign
        }
      }),
    );
    resetPageNumber();
  };

  const handleCountriesChange = (values) => {
    const updatedCountriesList = values.map(({ iso }) => iso);

    setSettings(
      produce(settings, (draftSettings) => {
        if (!newsletterEnabled) {
          draftSettings.newsFeed.countriesList = updatedCountriesList; // eslint-disable-line no-param-reassign
        } else {
          draftSettings.newsletter.countriesList = updatedCountriesList; // eslint-disable-line no-param-reassign
        }
      }),
    );
    resetPageNumber();
  };

  const handleFollowedAnalystsChange = () => (event) => {
    setSettings(
      produce(settings, (draftSettings) => {
        if (!newsletterEnabled) {
          draftSettings.newsFeed.limitToFollowedAnalysts = event.target.checked; // eslint-disable-line no-param-reassign
        } else {
          draftSettings.newsletter.limitToFollowedAnalysts = event.target.checked; // eslint-disable-line no-param-reassign
        }
      }),
    );
    resetPageNumber();
  };

  const newsTypeChecked = (newsType) => {
    const newsTypes = !newsletterEnabled
      ? settings.newsFeed.newsTypes
      : settings.newsletter.newsTypes;

    return newsToArtifactTypes[newsType].every((type) => newsTypes.includes(type));
  };

  const followedAnalystsChecked = () => {
    return !newsletterEnabled
      ? settings.newsFeed.limitToFollowedAnalysts
      : settings.newsletter.limitToFollowedAnalysts;
  };

  const selectedIndustries = () => {
    const industryTypes = !newsletterEnabled
      ? settings.newsFeed.industryTypes
      : settings.newsletter.industryTypes;

    return industries
      ? industries.filter((industry) =>
          industryTypes.some(
            (industryType) => industryType.code === industry.code || industryType === industry.code,
          ),
        )
      : [];
  };

  const selectedCountries = () => {
    const countriesIso = !newsletterEnabled
      ? settings.newsFeed.countriesList
      : settings.newsletter.countriesList;

    if (!countriesIso) {
      return [];
    }
    return allRegions.data.filter(({ iso }) => countriesIso.includes(iso));
  };

  useEffect(() => {
    setIndustries(flattenIndustries(industriesData));
  }, [industriesData]);

  return (
    <Paper className={classes.root}>
      {newsletterEnabled && (
        <>
          <FormControl className={classes.formControl} component="fieldset">
            <FormLabel component="legend" className={classes.label}>
              {t('Frequency')}
            </FormLabel>
            <RadioGroup
              className={classes.space}
              aria-label="frequency"
              name="frequency"
              value={settings.newsletter.frequency}
              onChange={handleFrequencyChange}
              row
            >
              <FormControlLabel
                value={newsletterFrequencies.WEEKLY}
                control={<Radio color="primary" />}
                label={t('Weekly')}
              />
              <FormControlLabel
                value={newsletterFrequencies.NONE}
                control={<Radio color="primary" />}
                label={t('None')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl className={classes.formControl} component="fieldset">
            <FormLabel component="legend" className={classes.label}>
              {t('News feed')}
            </FormLabel>
            <FormGroup className={clsx(classes.types, newsletterEnabled && classes.space)} row>
              <FormControlLabel
                disabled={disabled}
                control={
                  <Checkbox
                    checked={settings.newsletter.useNewsFeedPreferences}
                    onChange={handleNewsFeedCopyChange}
                    value="newsFeed"
                    color="primary"
                  />
                }
                label={t('Copy my personalised news feed settings to my newsletter settings')}
              />
            </FormGroup>
          </FormControl>
        </>
      )}
      {showNewsTypes && (
        <>
          <FormControl
            error={!disabled && error}
            className={classes.formControl}
            component="fieldset"
            required={!disabled}
          >
            <FormLabel component="legend" className={classes.label}>
              {t('News type')}
            </FormLabel>
            <FormGroup className={classes.space} row>
              <FormControlLabel
                disabled={disabled}
                control={
                  <Checkbox
                    checked={newsTypeChecked(newsFeedTypes.RATING_NEWS)}
                    onChange={handleTypeChange(newsFeedTypes.RATING_NEWS)}
                    value="ratingNews"
                    color="primary"
                  />
                }
                label={t(newsFeedTypesLabels.RATING_NEWS)}
              />
              <FormControlLabel
                disabled={disabled}
                control={
                  <Checkbox
                    checked={newsTypeChecked(newsFeedTypes.RESEARCH)}
                    onChange={handleTypeChange(newsFeedTypes.RESEARCH)}
                    value="research"
                    color="primary"
                  />
                }
                label={t(newsFeedTypesLabels.RESEARCH)}
              />
              <FormControlLabel
                disabled={disabled}
                control={
                  <Checkbox
                    checked={newsTypeChecked(newsFeedTypes.COMPANY_NEWS)}
                    onChange={handleTypeChange(newsFeedTypes.COMPANY_NEWS)}
                    value="companyNews"
                    color="primary"
                  />
                }
                label={t(newsFeedTypesLabels.COMPANY_NEWS)}
              />
            </FormGroup>
            {!disabled && (
              <FormHelperText className={classes.helperText}>
                {t('Please select at least one news type')}
              </FormHelperText>
            )}
          </FormControl>
          <div className={clsx((followedAnalysts || newsletterEnabled) && classes.space)}>
            <Autocomplete
              multiple
              disabled={disabled}
              options={industries || []}
              onChange={handleIndustriesChange}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.industryName}
              value={selectedIndustries()}
              renderInput={(params) => (
                <TextField {...params} label={t('Industries')} variant="outlined" />
              )}
              classes={groupedAutocompleteClasses}
            />
            <GroupedSelect
              multiple
              options={allRegions.data}
              value={selectedCountries()}
              label={t('Country')}
              onChange={handleCountriesChange}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.continent}
            />
          </div>
          {(followedAnalysts || newsletterEnabled) && (
            <>
              <FormControl className={classes.formControl} component="fieldset">
                <FormLabel component="legend" className={classes.label}>
                  {t('Followed analysts')}
                </FormLabel>
                <FormGroup className={classes.types} row>
                  <FormControlLabel
                    disabled={disabled}
                    control={
                      <Checkbox
                        checked={followedAnalystsChecked()}
                        onChange={handleFollowedAnalystsChange()}
                        value="followedAnalysts"
                        color="primary"
                      />
                    }
                    label={t('Limit to content from my followed analysts')}
                  />
                </FormGroup>
              </FormControl>
            </>
          )}
        </>
      )}
    </Paper>
  );
};

NewsFeedSettings.defaultProps = {
  followedAnalysts: false,
  newsletterEnabled: false,
  pristine: false,
  newsSettingsChanged: () => null,
  errorStateChanged: () => null,
};

NewsFeedSettings.propTypes = {
  followedAnalysts: PropTypes.bool,
  newsletterEnabled: PropTypes.bool,
  pristine: PropTypes.bool,
  newsSettingsChanged: PropTypes.func,
  errorStateChanged: PropTypes.func,
};

export default NewsFeedSettings;
